import {t} from "@lingui/core/macro";
import type {CSSProperties} from "aphrodite";
import * as React from "react";

import WBLink from "@khanacademy/wonder-blocks-link";
import {LabelLarge} from "@khanacademy/wonder-blocks-typography";
import {EMAIL_REGEX} from "@khan/auth-password";
import {PermissionsInfo, RequestInfo} from "@khan/shared-core";

// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedLink from "#components/link-package/deprecated-link.ts";
import {getKaid} from "#data-access/profile-utils.ts";
import type {ProfileModelType} from "#shared-package/profile-model.ts";
import {getPendingEmailToVerify} from "#site-banners/banners/verify-email/verify-email-utils.ts";

import * as sharedStyles from "./shared-styles.ts";
import type {
    ContentKind,
    DiscussionContext,
    FeedbackType,
    Post,
    PostKey,
    PostType,
} from "./types.ts";
import {fromGqlToPostType} from "./util/feedback-type-converters.ts";

type VoteEntityMutationErrorCode =
    | "CS_VOTE_NOT_ALLOWED"
    | "DOWNVOTE_NOT_ALLOWED"
    | "INTERNAL"
    | "SELFVOTE_NOT_ALLOWED"
    | "TOO_MANY_REQUESTS"
    | "UNAUTHORIZED"
    | "UPVOTE_NOT_ALLOWED";

export const getAriaAvatarName = (avatarName?: string | null): string => {
    return avatarName ? avatarName.replace(/_/g, " ") : "";
};

export const getAnswerInputId = (key: string): string => {
    return `${key}-answer-input`;
};

export const loggedInQueryName = "loggedInViaDiscussion";

export const userHasJustLoggedIn = (
    location: Record<string, string>,
): boolean => {
    return location.href.indexOf(`${loggedInQueryName}=true`) !== -1;
};

export const isPostUnanswered = (
    postKey: PostKey,
    posts: Record<string, Post>,
): boolean => {
    const post = posts[postKey];

    if (!post || post.deleted || post.appearsAsDeleted) {
        return false;
    }
    const answers = post.answers;
    if (!answers.length) {
        return true;
    }
    // Answers that aren't visible to non-mods don't count as valid answers.
    const hasVisibleAnswer = answers.some((answerKey) => {
        const answer = posts[answerKey];
        return !answer.deleted && !answer.appearsAsDeleted;
    });
    return !hasVisibleAnswer;
};

type PostAuthorNameProps = {
    kaid?: string;
    name: string;
};
export const PostAuthorName = (
    props: PostAuthorNameProps,
): React.ReactElement<React.ComponentProps<"div">> => {
    const {kaid, name} = props;

    const nameLabel = (
        <LabelLarge style={sharedStyles.authorNickname}>{name}</LabelLarge>
    );

    // TODO(diedra): Verify is this is still necessary.
    // We're using a div tag here to stop JAWS from merging this with any strings
    // that may follow it.
    return (
        <div>
            {kaid ? (
                <DeprecatedLink
                    href={`/profile/${kaid}/discussion`}
                    aria-label={t`Go to ${name}'s user profile.`}
                >
                    {nameLabel}
                </DeprecatedLink>
            ) : (
                nameLabel
            )}
        </div>
    );
};

type EnglishLinkProps = {
    style: CSSProperties;
};
export const EnglishLink = (
    props: EnglishLinkProps,
): React.ReactElement | null => {
    if (RequestInfo.kaLocale === "en") {
        return null;
    }

    // TODO(jangmi, LP-7436): Use withLocation to access SSR safe location.
    // eslint-disable-next-line no-restricted-syntax
    const {pathname, search, hash} = window.location;
    // We're using the en subdomain instead of the ?lang=en flag to reduce confusion.
    // Users will see content on the English version of the site that doesn't exist in the
    // language that they were in previously. If we use the ?lang=en flag, and the
    // user clicks on a piece of content that isn't available in their subdomain,
    // they'll be directed to a 404 page, which is less than ideal.
    const href = `https://en.khanacademy.org${pathname}${search}${hash}`;

    return (
        <WBLink href={href} style={props.style}>
            {t`Do you understand English? Click here to see more discussion happening on Khan Academy's English site.`}
        </WBLink>
    );
};

export const shouldAutoExpandAnswers = (
    post: Post,
    qaExpandKey?: string | null,
): boolean => {
    const {answers} = post;

    if (!qaExpandKey || !answers) {
        return false;
    }

    return answers.includes(qaExpandKey);
};

export const shouldAutoExpandReplies = (
    post: Post,
    qaExpandKey?: string | null,
): boolean => {
    const {replyExpandKeys} = post;

    if (!qaExpandKey || !replyExpandKeys) {
        return false;
    }

    return replyExpandKeys.includes(qaExpandKey);
};

/**************************************************************
 * Post capabilities
 **************************************************************/

// Summarizes information about a post that affects which capabilities are enabled.
// Having this intermediate type makes it much easier to write and test helper functions
// without depending on KAGlobals, etc.
export type PostContext = {
    user: User;
    post: Post;
    view: DiscussionView;
};

export function getPostContext({
    profile,
    post,
    moderatorMode,
}: {
    profile: ProfileModelType;
    post: Post;
    moderatorMode: boolean;
}): PostContext {
    const user = makeUser(profile);
    const view = makeView({moderatorMode});
    return {
        user,
        post,
        view,
    };
}

export const isModeratorProfile = (
    profile?: ProfileModelType | null,
): boolean => Boolean(profile && profile.get("isModerator")); // TODO(WEB-1092): Add isModerator() to ProfileModel

// TODO(WEB-1094): Add isChildAccount() to ProfileModel and simplify the param type here
export const makeUser = (
    profile: ProfileModelType,
):
    | {
          isChild: boolean;
          isModerator: boolean;
          status: "LOGGED_IN";
      }
    | {
          status: "LOGGED_OUT";
      } => {
    if (!profile) {
        return {status: "LOGGED_OUT"};
    }
    return {
        status: "LOGGED_IN",
        isModerator: isModeratorProfile(profile),
        isChild: profile.get("isChildAccount"),
    };
};

export const makeView = ({
    moderatorMode,
}: {
    moderatorMode: boolean;
}):
    | {
          where: "CONTENT";
      }
    | {
          where: "FLAG_QUEUE";
      } => {
    // TODO(slim): Add more cases.
    if (moderatorMode) {
        return {where: "FLAG_QUEUE"};
    }
    return {where: "CONTENT"};
};

/**
 * PostContext predicates
 */

// Current user.
type User =
    | {
          status: "LOGGED_OUT";
      }
    | {
          status: "LOGGED_IN";
          isChild: boolean;
          isModerator: boolean;
      };

const isModerator = (user: User) =>
    user.status === "LOGGED_IN" && user.isModerator;

// Only the properties we need from Post.
type TestPost = {
    flags?: Post["flags"];
    definitelyNotSpam?: Post["definitelyNotSpam"];
};

const hasFlags = (post: TestPost) => !!post.flags && post.flags.length > 0;
const hasNoFlags = (post: TestPost) => !hasFlags(post);
const definitelyNotSpam = (post: TestPost) => post.definitelyNotSpam;
const mightBeSpam = (post: TestPost) => !definitelyNotSpam(post);

// Where on the site the post is being rendered.
type DiscussionView = // Article, Video, etc.
    // Moderator flag queue.
    | {
          where: "CONTENT";
      }
    | {
          where: "FLAG_QUEUE";
      };

const isFlagQueue = (view: DiscussionView) => view.where === "FLAG_QUEUE";

// Compute which capabilities are enabled.
export const capabilities: Record<string, (arg1: PostContext) => boolean> = {
    clearFlags: ({user, post}) => isModerator(user) && hasFlags(post),

    approve: ({user, post, view}) =>
        isFlagQueue(view) &&
        isModerator(user) &&
        mightBeSpam(post) &&
        hasNoFlags(post),
};

/**************************************************************
 * Screen reader text
 **************************************************************/

/**
 * A method that gets the screen reader explanation string for the link that goes directly
 * to a specific post.
 */
export const getDirectLinkSrString = (timeago: string, post: Post): string => {
    const {author, authorNickname, title, content} = post;

    // `username` was the original variable name used in these strings, and changing it
    // may trigger a retranslation, so we may the authorNickname value back to username here.
    const username = authorNickname || author?.nickname || "";

    // Only projects have titles. The other posts are identified by their content.
    const postContent = title ? title : content;
    // We want the first 25 characters if the post has that many. Otherwise we'll just take whatever's available.
    const openingText = postContent.slice(0, 25);
    const truncated = postContent.length > 25;

    if (truncated) {
        return t`Posted ${timeago}. Direct link to ${username}'s post “${openingText}...”`;
    }
    return t`Posted ${timeago}. Direct link to ${username}'s post “${openingText}”`;
};

/**
 * A method that gets the screen reader explanation string for the response button on
 * user's posts.
 */
export const getRespondButtonSrString = (
    post: Post,
    postType: "reply" | "comment" | "answer",
): string => {
    const {authorNickname, title, content} = post;

    // `username` was the original variable name used in these strings, and changing it
    // may trigger a retranslation, so we may the authorNickname value back to username here.
    const username = authorNickname;

    // Only projects have titles. The other posts are identified by their content.
    const postContent = title ? title : content;
    // We want the first 25 characters if the post has that many. Otherwise we'll just take whatever's available.
    const openingText = postContent.slice(0, 25);
    const truncated = postContent.length > 25;

    if (postType === "reply") {
        if (truncated) {
            return t`Reply to ${username}'s post “${openingText}...”`;
        }
        return t`Reply to ${username}'s post “${openingText}”`;
    }
    if (postType === "comment") {
        if (truncated) {
            return t`Comment on ${username}'s post “${openingText}...”`;
        }
        return t`Comment on ${username}'s post “${openingText}”`;
    }
    if (truncated) {
        return t`Answer ${username}'s post “${openingText}...”`;
    }
    return t`Answer ${username}'s post “${openingText}”`;
};

/**
 * Determine if the current user is restricted from interacting with the discussions feature,
 * ie submiting posts and voting on other posts.
 */
export const getDiscussionRestrictions = (
    profile?: ProfileModelType | null,
    forceRestrictPosting = true,
): DiscussionContext => {
    if (!profile || !profile.get || !PermissionsInfo.isLoggedIn) {
        return {restrictPosting: forceRestrictPosting};
    }

    const POINTS_REQUIRED_TO_POST = 5000;

    const isModerator = profile.get("isModerator");
    const points = profile.get("points");
    const notEnoughPoints = !isModerator && points < POINTS_REQUIRED_TO_POST;
    const emailToVerify = getPendingEmailToVerify(profile);

    const joined = new Date(profile.get("joined"));
    const now = Date.now();
    const THREE_DAYS = 2.592e8;
    const meetsAccountAgeRequirement = joined <= new Date(now - THREE_DAYS);

    return {
        restrictPosting: notEnoughPoints || forceRestrictPosting,
        notEnoughPoints,
        unverifiedEmail: emailToVerify && emailToVerify.email,
        meetsAccountAgeRequirement,
    };
};

/**
 * Determine if the current user meets community posting/voting/flagging
 * requirements.
 */
export const userMeetsCommunityRequirements = (
    profile?: ProfileModelType | null,
): boolean => {
    const restrictions = getDiscussionRestrictions(profile);
    const isVerifiedEmailPresent: boolean = profile
        ? profile.get("authEmails").length > 0
        : false;
    return (
        restrictions &&
        !restrictions.unverifiedEmail &&
        // Unverified email could be null if no email provided at all;
        // need to confirm user has an authorized email
        isVerifiedEmailPresent &&
        userHasEnoughPoints(restrictions) &&
        userMeetsAccountAgeRequirement(restrictions)
    );
};

/**
 * Get a modified version of the context for a discussion based on the user.
 * We're specifically adding information about whether the user is restricted
 * from interacting with discussions based on their account status (such as
 * moderation privileges and number of points earned).
 */
export const getModifiedDiscussionContext = (
    profile: ProfileModelType | null | undefined,
    discussionContext: DiscussionContext,
): DiscussionContext => {
    return {
        ...discussionContext,
        ...getDiscussionRestrictions(
            profile,
            discussionContext.restrictPosting,
        ),
    };
};

/**************************************************************
 * Utils for determining which state a user sees
 **************************************************************/
export const getUserUnverifiedEmail = (
    discussionContext: DiscussionContext,
): string | null | undefined => {
    return discussionContext.unverifiedEmail;
};

export const userIsLoggedIn = (): boolean => {
    return PermissionsInfo.isLoggedIn;
};

export const userHasEnoughPoints = (
    discussionContext: DiscussionContext,
): boolean => {
    return !discussionContext.notEnoughPoints;
};

export const userMeetsAccountAgeRequirement = (
    discussionContext: DiscussionContext,
): boolean => {
    return discussionContext.meetsAccountAgeRequirement ?? false;
};

export const userRestrictedPosting = (
    discussionContext: DiscussionContext,
): undefined | boolean => {
    return discussionContext.restrictPosting;
};

export const userIsScratchpadAuthor = (
    contentKind: ContentKind | null | undefined,
    discussionContext: DiscussionContext,
    profile?: ProfileModelType | null,
): boolean => {
    return (
        contentKind === "scratchpad" &&
        discussionContext.authorKaid === getKaid(profile)
    );
};

export const userIsScratchpadAuthorViewingQuestionsTab = (
    currentTab: FeedbackType,
    contentKind: ContentKind | null | undefined,
    discussionContext: DiscussionContext,
    profile?: ProfileModelType | null,
): boolean => {
    return (
        currentTab === "questions" &&
        userIsScratchpadAuthor(contentKind, discussionContext, profile)
    );
};

export const userIsScratchpadAuthorViewingOpenHelpRequests = (
    currentTab: FeedbackType,
    contentKind: ContentKind | null | undefined,
    discussionContext: DiscussionContext,
    profile?: ProfileModelType | null,
    hasUnansweredHelpRequests?: boolean | null,
): boolean => {
    return (
        currentTab === "projecthelp" &&
        userIsScratchpadAuthor(contentKind, discussionContext, profile) &&
        hasUnansweredHelpRequests === true
    );
};

export const userIsScratchpadAuthorViewingLoadingHelpRequests = (
    currentTab: FeedbackType,
    contentKind: ContentKind | null | undefined,
    discussionContext: DiscussionContext,
    profile?: ProfileModelType | null,
    hasUnansweredHelpRequests?: boolean | null,
): boolean => {
    // A null or undefined value here means that the posts for this tab haven't been fetched yet.
    return (
        currentTab === "projecthelp" &&
        userIsScratchpadAuthor(contentKind, discussionContext, profile) &&
        (hasUnansweredHelpRequests === null ||
            hasUnansweredHelpRequests === undefined)
    );
};

/**
 * This one is confusing, but it's trying to determine if someone is looking at a scratchpad's
 * help requests tab and is someone other than the author.
 */
export const userNotScratchpadAuthorViewingHelpRequests = (
    currentTab: FeedbackType,
    contentKind: ContentKind | null | undefined,
    discussionContext: DiscussionContext,
    profile?: ProfileModelType | null,
): boolean => {
    return (
        currentTab === "projecthelp" &&
        contentKind === "scratchpad" &&
        !userIsScratchpadAuthor(contentKind, discussionContext, profile)
    );
};

export const showMainDiscussionInput = (
    currentTab: FeedbackType,
    contentKind: ContentKind | null | undefined,
    discussionContext: DiscussionContext,
    profile?: ProfileModelType | null,
    hasUnansweredHelpRequests?: boolean | null,
): boolean => {
    return (
        userIsLoggedIn() &&
        userHasEnoughPoints(discussionContext) &&
        userMeetsAccountAgeRequirement(discussionContext) &&
        !getUserUnverifiedEmail(discussionContext) &&
        !userRestrictedPosting(discussionContext) &&
        !userIsScratchpadAuthorViewingQuestionsTab(
            currentTab,
            contentKind,
            discussionContext,
            profile,
        ) &&
        !userIsScratchpadAuthorViewingOpenHelpRequests(
            currentTab,
            contentKind,
            discussionContext,
            profile,
            hasUnansweredHelpRequests,
        ) &&
        !userIsScratchpadAuthorViewingLoadingHelpRequests(
            currentTab,
            contentKind,
            discussionContext,
            profile,
            hasUnansweredHelpRequests,
        ) &&
        !userNotScratchpadAuthorViewingHelpRequests(
            currentTab,
            contentKind,
            discussionContext,
            profile,
        )
    );
};

export const hideExternalLinks = (bio?: string | null): string => {
    // We don't want to show URLS or email addresses in bios.
    // Mask them with a regexp replacement
    if (!bio) {
        return "";
    }

    // https://stackoverflow.com/questions/3809401/what-is-a-good-regular-expression-to-match-a-url
    const urlRegex =
        /(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-zA-Z]{2,62}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g;
    const replacementText = "***";

    return bio
        .replace(EMAIL_REGEX, replacementText)
        .replace(urlRegex, replacementText);
};

export const getVoteEntityErrorMessage: (err: {
    code: VoteEntityMutationErrorCode;
}) => string = ({code}) => {
    switch (code) {
        case "UNAUTHORIZED":
            return t`Log in first!`;
        case "UPVOTE_NOT_ALLOWED":
            return t`To upvote, your account must be at least 3 days old, have a verified email address, and have at least 5,000 energy points.`;
        case "DOWNVOTE_NOT_ALLOWED":
            return t`You need at least 5,000 energy points to down vote.`;
        case "TOO_MANY_REQUESTS":
            return t`There have been too many requests to this page recently. Try again in a little while.`;
        case "SELFVOTE_NOT_ALLOWED":
            return t`You cannot vote for your own content.`;
        case "CS_VOTE_NOT_ALLOWED":
            return t`You cannot downvote CS programs.`;
        default:
            return t`Oops! Something went wrong. Please try again later.`;
    }
};

// getPostType is only necessary while we are transitioning from REST to GQL. once all REST calls have been removed, we should switch to referencing feedbackType field only.
export const getPostType = ({feedbackType, type}: Post): PostType =>
    feedbackType ? fromGqlToPostType(feedbackType) : type;

// Supports discussion-post when rendered on the flagged feedback page. Programs
// are weirdly treated as feedback on that page although they are not feedback.
// This allows us to render them without getting errors from the getPostType
// function
export const getPostTypeAllowingProgram = ({
    feedbackType,
    type,
}: Post): PostType => {
    if (feedbackType && feedbackType === "program") {
        return "scratchpad";
    }
    return feedbackType ? fromGqlToPostType(feedbackType) : type;
};
